import { Rng } from '@/core/rng';
import type { ItemId } from '@/data/items';
import type { NpcId } from '@/data/npcs';
import {
  REQUESTS_PER_DAY,
  REQUEST_FRIENDSHIP,
  REQUEST_TEMPLATES,
  type RequestTemplate,
} from '@/data/requests';
import type { Friendships } from '@/world/friendship';
import type { Inventory } from '@/world/inventory';
import type { Wallet } from '@/world/wallet';

/**
 * The notice board.
 *
 * What is pinned up derives from the seed and the day, like the weather and
 * like a dungeon floor: nothing about a posting has to be saved, and the same
 * farm on the same morning asks the same things on any device. What *is*
 * saved is the one thing that cannot be derived — which of today's requests
 * you have already filled.
 *
 * The postings are replaced each morning, so yesterday's filled list is dead
 * weight. Keeping only the current day means the save cannot grow without
 * bound however long a farm is played.
 */

/** Salted apart from the dungeon streams so adding a floor roll cannot move the board. */
const BOARD_SALT = 0x51;

export interface PostedRequest {
  readonly template: RequestTemplate;
  readonly id: string;
  readonly from: NpcId;
  readonly item: ItemId;
  readonly count: number;
  /** Coins for the whole delivery. */
  readonly pay: number;
  readonly note: string;
}

export type FillResult = 'filled' | 'short' | 'already-filled' | 'unknown';

export interface FillContext {
  readonly inventory: Inventory;
  readonly wallet: Wallet;
  readonly friendships: Friendships;
}

export class RequestBoard {
  private day = -1;
  private readonly filled = new Set<string>();

  constructor(private readonly seed: number) {}

  /** What is pinned up on a given day. */
  postings(dayIndex: number): PostedRequest[] {
    const rng = Rng.forStream(this.seed, dayIndex, BOARD_SALT);
    // Shuffled rather than picked one at a time, so the same request cannot go
    // up twice on the same morning.
    const chosen = rng.shuffle(REQUEST_TEMPLATES).slice(0, REQUESTS_PER_DAY);

    return chosen.map((template) => {
      const count = rng.int(template.least, template.most);
      return {
        template,
        id: template.id,
        from: template.from,
        item: template.item,
        count,
        pay: template.payPer * count,
        note: template.note,
      };
    });
  }

  isFilled(id: string, dayIndex: number): boolean {
    return this.day === dayIndex && this.filled.has(id);
  }

  /**
   * Hand over the goods.
   *
   * Nothing leaves the backpack unless the whole request can be met: a
   * half-filled delivery would take the items and pay nothing, which is the
   * worst outcome available.
   */
  fill(id: string, dayIndex: number, context: FillContext): FillResult {
    const posting = this.postings(dayIndex).find((request) => request.id === id);
    if (!posting) return 'unknown';
    if (this.isFilled(id, dayIndex)) return 'already-filled';
    if (context.inventory.countOf(posting.item) < posting.count) return 'short';

    context.inventory.remove(posting.item, posting.count);
    context.wallet.earn(posting.pay);
    context.friendships.add(posting.from, REQUEST_FRIENDSHIP);

    this.rollOver(dayIndex);
    this.filled.add(id);
    return 'filled';
  }

  /** Forget yesterday. A new morning is a new board. */
  private rollOver(dayIndex: number): void {
    if (this.day === dayIndex) return;
    this.day = dayIndex;
    this.filled.clear();
  }

  toSave(): { day: number; filled: string[] } {
    return { day: this.day, filled: [...this.filled] };
  }

  static fromSave(seed: number, value: unknown): RequestBoard {
    const board = new RequestBoard(seed);
    if (!value || typeof value !== 'object') return board;

    const raw = value as { day?: unknown; filled?: unknown };
    const day = Math.floor(Number(raw.day));
    if (!Number.isFinite(day)) return board;

    board.day = day;
    if (Array.isArray(raw.filled)) {
      for (const id of raw.filled) {
        if (typeof id === 'string') board.filled.add(id);
      }
    }
    return board;
  }
}
